import { defineStore } from 'pinia'
import axios from 'axios'
import apiClient from '../api/client'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    token: localStorage.getItem('token') || null,
    refreshToken: localStorage.getItem('refresh_token') || null,
    masqueradingAs: null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    isSuperAdmin: (state) => state.user?.roles?.super_admin || false,
    isSuperCurator: (state) => state.user?.roles?.super_curator || false,
    isCurator: (state) => state.user?.roles?.curator || false,
    isTrailCreator: (state) => state.user?.roles?.trail_creator || false,
    isTrustedTrailCreator: (state) => state.user?.roles?.trusted_trail_creator || false,
    hasUsername: (state) => !state.user || !!state.user.username,
    isMasquerading: (state) => !!state.masqueradingAs,
  },

  actions: {
    applyTokens({ token, refresh_token }) {
      this.token = token
      this.refreshToken = refresh_token || null
      localStorage.setItem('token', token)
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token)
      } else {
        localStorage.removeItem('refresh_token')
      }
    },

    clearTokens() {
      this.token = null
      this.refreshToken = null
      localStorage.removeItem('token')
      localStorage.removeItem('refresh_token')
    },

    async login(email, password) {
      const response = await apiClient.post('/auth/login', { email, password })
      this.applyTokens(response.data)
      this.user = response.data.user
    },

    async logout({ localOnly = false } = {}) {
      if (!localOnly) {
        try {
          await apiClient.delete('/auth/logout')
        } catch {
          // Continue with local logout even if the server rejects the request
          // (e.g. token already expired)
        }
      }
      this.user = null
      this.masqueradingAs = null
      this.clearTokens()
    },

    // Bare axios call — never goes through apiClient — so a 401 response from
    // the refresh endpoint itself does not re-enter the response interceptor's
    // refresh logic and loop. Throws on non-2xx so the caller (interceptor)
    // can fall through to handleSessionExpired.
    async refresh() {
      if (!this.refreshToken) {
        throw new Error('no_refresh_token')
      }
      const response = await axios.post(
        '/api/v1/auth/refresh',
        { refresh_token: this.refreshToken },
        { headers: { 'Content-Type': 'application/json' } },
      )
      this.applyTokens(response.data)
      return response.data
    },

    async fetchUser() {
      if (!this.token) {
        return
      }

      const response = await apiClient.get('/users/me')
      this.user = response.data
      this.masqueradingAs = response.data?.masquerading_as || null
    },

    async stopMasquerade() {
      if (!this.masqueradingAs) return
      const sessionId = this.masqueradingAs.session_id
      try {
        await apiClient.delete(`/auth/masquerade_sessions/${sessionId}`)
      } catch {
        // Session may already be ended server-side; continue client-side cleanup either way.
      }
      this.user = null
      this.masqueradingAs = null
      this.clearTokens()
    },
  },
})
